import LeadsIcon from '@forwardflow/assets/svg/LeadIcon'
import { useGetDashboardChartsOverview } from '@forwardflow/hooks/customHooks'
import { SourceInfo, TimePeriod } from '@forwardflow/types'
import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

const colorPalette = ['#60A5FA', '#3B82F6', '#2563EB', '#1E40AF', '#1b3487']

const SecondaryCard: React.FC = () => {
   const [searchParams] = useSearchParams()
   const timePeriod = searchParams.get('timePeriod') || 'allTime'
   const [hoveredSegment, setHoveredSegment] = useState<number | null>(null)
   const radius = 100

   const {
      data: DashboardChartsOverview,
      isLoading: DashboardChartsOverviewLoading,
   } = useGetDashboardChartsOverview({
      timePeriod: timePeriod as TimePeriod,
   })

   if (DashboardChartsOverviewLoading) {
      return (
         <div className="flex flex-col h-full p-4 rounded-lg sm:px-6 sm:py-4 bg-white/5">
            <div className="flex items-center justify-center h-full"></div>
         </div>
      )
   }

   const {
      total: totalLeads = 0,
      change,
      sources = [],
      displayTitle = 'New Leads',
   } = DashboardChartsOverview?.secondaryCard || {}
   const { percent: growthPercentage = 0, isIncrement } = change || {}

   let cumulativePercentage = 0

   return (
      <div className="flex flex-col h-full p-4 rounded-lg sm:px-6 sm:py-4 bg-white/5">
         <div className="flex items-center justify-center flex-1 w-full">
            {/* Donut Chart */}
            <div className="relative flex justify-center w-full px-4 md:w-1/2">
               <svg
                  viewBox="0 0 240 240"
                  className="w-full h-auto max-w-full mx-auto transform"
                  style={{
                     width: '100%',
                     height: 'auto',
                     maxWidth: '240px',
                  }}
               >
                  {sources.length === 0 ||
                  sources.every((s: SourceInfo) => s.percent === 0) ? (
                     <circle
                        cx={120}
                        cy={120}
                        r={110}
                        fill="transparent"
                        stroke="#6B7280"
                        opacity={0.5}
                        strokeWidth={20}
                     />
                  ) : (
                     sources.map((source: SourceInfo, index: number) => {
                        const minStroke = 10
                        const maxStroke = 20
                        const minPercent = Math.min(
                           ...sources.map((s: SourceInfo) => s.percent)
                        )
                        const maxPercent = Math.max(
                           ...sources.map((s: SourceInfo) => s.percent)
                        )
                        const dynamicStrokeWidth =
                           minStroke +
                           ((source.percent - minPercent) /
                              (maxPercent - minPercent || 1)) *
                              (maxStroke - minStroke)

                        const normalizedRadius = radius + dynamicStrokeWidth / 2
                        const circumference = normalizedRadius * 2 * Math.PI
                        const strokeDasharray = `${
                           (source.percent / 100) * circumference
                        } ${circumference}`
                        const strokeDashoffset =
                           (-cumulativePercentage * circumference) / 100

                        cumulativePercentage += source.percent

                        return (
                           <circle
                              key={index}
                              stroke={colorPalette[index]}
                              fill="transparent"
                              strokeWidth={dynamicStrokeWidth}
                              strokeDasharray={strokeDasharray}
                              strokeDashoffset={strokeDashoffset}
                              r={normalizedRadius}
                              cx={120}
                              cy={120}
                              className="transition-opacity duration-200"
                              opacity={
                                 hoveredSegment === null ||
                                 hoveredSegment === index
                                    ? 1
                                    : 0.5
                              }
                              style={{
                                 strokeLinecap: 'butt',
                                 pointerEvents: 'none',
                              }}
                              transform="rotate(-90 120 120)"
                           />
                        )
                     })
                  )}
               </svg>

               {/* Center content */}
               <div className="absolute inset-0 flex flex-col items-center justify-center flex-1 pointer-events-none">
                  <LeadsIcon />
                  <div className="text-sm font-semibold text-white sm:mt-3 sm:mb-0 sm:text-2xl">
                     {totalLeads.toLocaleString()}
                  </div>
                  <div className="mb-2 text-xs text-white/70 sm:text-sm">
                     {displayTitle}
                  </div>
                  {change && (
                     <span
                        className={`${
                           isIncrement ? 'text-[#00C2A8]' : 'text-red-500'
                        } text-sm font-medium hidden sm:block`}
                     >
                        {isIncrement ? '+' : '-'}
                        {growthPercentage}%
                     </span>
                  )}
               </div>
            </div>

            {/* Legend */}
            <div className="flex flex-col w-full space-y-1 md:space-y-2 md:w-1/2 md:ml-8">
               {sources.map((source: SourceInfo, index: number) => (
                  <div
                     key={index}
                     className="flex items-center justify-between min-w-[180px] py-1.5 px-3 rounded md:rounded-lg transition-opacity duration-200 cursor-pointer"
                     style={{
                        backgroundColor: `${colorPalette[index]}50`,
                        opacity:
                           hoveredSegment === null || hoveredSegment === index
                              ? 1
                              : 0.5,
                     }}
                     onMouseEnter={() => setHoveredSegment(index)}
                     onMouseLeave={() => setHoveredSegment(null)}
                  >
                     <div className="flex items-center">
                        <div
                           className="w-3 h-3 mr-3 rounded-full"
                           style={{ backgroundColor: colorPalette[index] }}
                        />
                        <span className="text-xs text-gray-300 md:text-sm">
                           {source.name}
                        </span>
                     </div>
                     <div className="flex flex-col text-right">
                        <span className="text-xs font-medium text-white md:text-sm">
                           {source.count.toLocaleString()}
                        </span>
                        <span className="hidden text-xs text-gray-500 sm:block">
                           {source.percent}%
                        </span>
                     </div>
                  </div>
               ))}
            </div>
         </div>
      </div>
   )
}

export default SecondaryCard
